/**
 * Upload Service
 *
 * Handles uploading recordings to S3 via presigned URLs.
 *
 * New API Flow (v0.13.0+):
 * 1. Create inspection via POST /api/inspections (with vehicleId)
 * 2. Get presigned URL via POST /api/upload-url (with inspectionId)
 * 3. Upload file to S3
 * 4. Finalize inspection via POST /api/inspections/{id}/finalize
 */

import apiClient from './apiClient';
import { settings } from './settingsService';

/**
 * Generate timestamp string for filename (YYYY_MM_DD_HH_MM_SS format)
 * @returns {string} Formatted timestamp
 */
const generateTimestamp = () => {
  const now = new Date();
  const yyyy = String(now.getFullYear());
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return `${yyyy}_${mm}_${dd}_${hh}_${min}_${ss}`;
};

/**
 * Capture device information for analytics
 * @param {string} inputDeviceName - Optional input device name from MediaRecorder
 * @returns {{inputDevice: string|null, os: string, browser: string, isMobile: boolean}}
 */
export const captureDeviceInfo = (inputDeviceName = null) => {
  const ua = navigator.userAgent;

  // Detect OS
  let os = 'Unknown';
  if (/Windows NT 10/.test(ua)) os = 'Windows 10';
  else if (/Windows NT 11|Windows NT 10.*Windows 11/.test(ua)) os = 'Windows 11';
  else if (/Mac OS X ([0-9_]+)/.test(ua)) {
    const match = ua.match(/Mac OS X ([0-9_]+)/);
    os = `macOS ${match[1].replace(/_/g, '.')}`;
  }
  else if (/iPhone OS ([0-9_]+)/.test(ua)) {
    const match = ua.match(/iPhone OS ([0-9_]+)/);
    os = `iOS ${match[1].replace(/_/g, '.')}`;
  }
  else if (/iPad.*OS ([0-9_]+)/.test(ua)) {
    const match = ua.match(/OS ([0-9_]+)/);
    os = `iPadOS ${match[1].replace(/_/g, '.')}`;
  }
  else if (/Android ([0-9.]+)/.test(ua)) {
    const match = ua.match(/Android ([0-9.]+)/);
    os = `Android ${match[1]}`;
  }
  else if (/Linux/.test(ua)) os = 'Linux';

  // Detect browser
  let browser = 'Unknown';
  if (/Edg\/([0-9.]+)/.test(ua)) {
    const match = ua.match(/Edg\/([0-9.]+)/);
    browser = `Edge ${match[1].split('.')[0]}`;
  }
  else if (/Chrome\/([0-9.]+)/.test(ua) && !/Edg/.test(ua)) {
    const match = ua.match(/Chrome\/([0-9.]+)/);
    browser = `Chrome ${match[1].split('.')[0]}`;
  }
  else if (/Safari\/([0-9.]+)/.test(ua) && !/Chrome/.test(ua)) {
    const match = ua.match(/Version\/([0-9.]+)/);
    browser = match ? `Safari ${match[1].split('.')[0]}` : 'Safari';
  }
  else if (/Firefox\/([0-9.]+)/.test(ua)) {
    const match = ua.match(/Firefox\/([0-9.]+)/);
    browser = `Firefox ${match[1].split('.')[0]}`;
  }

  // Detect mobile
  const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  return {
    inputDevice: inputDeviceName || null,
    os,
    browser,
    isMobile
  };
};

/**
 * Create a new inspection
 *
 * This must be called BEFORE uploading any files.
 *
 * @param {string|null} vehicleId - Vehicle identifier (optional)
 * @param {string|null} title - Display title for the inspection (optional, auto-generated if not provided)
 * @param {object} deviceInfo - Optional device information for analytics
 * @returns {Promise<{inspectionId: string, vehicleId: string|null, title: string, status: string, createdAt: string}>}
 */
export const createInspection = async (vehicleId, title = null, deviceInfo = null) => {
  try {
    const payload = {};
    if (vehicleId) payload.vehicleId = vehicleId;
    if (title) payload.title = title;
    if (deviceInfo) payload.deviceInfo = deviceInfo;
    const response = await apiClient.post('/inspections', payload);
    console.log('Inspection created:', response.data.inspectionId, 'title:', response.data.title);
    return response.data;
  } catch (error) {
    console.error('Error creating inspection:', error);
    throw error.response?.data?.detail
      ? new Error(error.response.data.detail)
      : error;
  }
};

/**
 * Update an inspection's vehicleId and/or title
 *
 * Only allowed for PENDING inspections (before transcription starts).
 *
 * @param {string} inspectionId - Inspection UUID
 * @param {object} updates - Fields to update: { vehicleId?, title? }
 * @returns {Promise<object>} Updated inspection
 */
export const updateInspection = async (inspectionId, updates) => {
  try {
    const response = await apiClient.patch(`/inspections/${inspectionId}`, updates);
    console.log('Inspection updated:', inspectionId, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating inspection:', error);
    throw error.response?.data?.detail
      ? new Error(error.response.data.detail)
      : error;
  }
};

/**
 * Get presigned URL from API Gateway
 *
 * @param {string} inspectionId - Inspection UUID (from createInspection)
 * @param {string} filename - Filename without extension
 * @param {string} mimeType - MIME type (default: audio/webm)
 * @returns {Promise<{uploadUrl: string, key: string, bucket: string, expiresIn: number}>}
 */
export const getPresignedUrl = async (inspectionId, filename, mimeType = 'audio/webm') => {
  try {
    const response = await apiClient.post('/upload-url', {
      inspectionId,
      filename,
      mimeType
    });
    return response.data;
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    throw error.response?.data?.detail
      ? new Error(error.response.data.detail)
      : error;
  }
};

/**
 * Update segment metadata after upload (duration, silence removed, etc.)
 *
 * @param {string} inspectionId - Inspection UUID
 * @param {string} filename - Segment filename (e.g., '2026_02_03_15_53_29_recording.webm')
 * @param {object} metadata - Metadata to update
 * @param {number} metadata.durationSec - Duration in seconds after processing
 * @param {number} metadata.originalDurationSec - Original duration before silence removal
 * @param {number} metadata.silenceRemovedSec - Seconds of silence removed
 * @returns {Promise<void>}
 */
export const updateSegmentMetadata = async (inspectionId, filename, metadata) => {
  console.log('[updateSegmentMetadata] Called with:', { inspectionId, filename, metadata });
  try {
    const response = await apiClient.patch(`/inspections/${inspectionId}/segments/${filename}`, {
      durationSec: metadata.durationSec,
      originalDurationSec: metadata.originalDurationSec,
      silenceRemovedSec: metadata.silenceRemovedSec
    });
    console.log(`[updateSegmentMetadata] Success for ${filename}:`, response.data);
  } catch (error) {
    // Don't fail the upload if metadata update fails - it's not critical
    console.error('[updateSegmentMetadata] Failed:', error.response?.data || error.message, error);
  }
};

/**
 * Report segment error to backend for troubleshooting
 *
 * @param {string} inspectionId - Inspection UUID
 * @param {string} filename - Segment filename
 * @param {string} errorMessage - Detailed error message
 * @returns {Promise<void>}
 */
export const reportSegmentError = async (inspectionId, filename, errorMessage) => {
  console.log('[reportSegmentError] Called with:', { inspectionId, filename, errorMessage });
  try {
    const errorTimestamp = new Date().toISOString();
    await apiClient.patch(`/inspections/${inspectionId}/segments/${filename}`, {
      status: 'FAILED',
      errorMessage,
      errorTimestamp
    });
    console.log(`[reportSegmentError] Error reported for ${filename}`);
  } catch (error) {
    // Log but don't throw - error reporting is best-effort
    console.error('[reportSegmentError] Failed to report error:', error.response?.data || error.message);
  }
};


/**
 * Finalize an inspection (triggers concatenation and transcription)
 *
 * Call this AFTER all chunks have been uploaded.
 * Handles 202 Accepted responses by retrying until processing completes.
 *
 * @param {string} inspectionId - Inspection UUID
 * @param {string[]} chunkFilenames - Optional ordered list of chunk filenames
 * @param {object} deviceInfo - Optional device information for analytics
 * @param {number} maxRetries - Maximum retry attempts for 202 responses (default: 30)
 * @param {number} retryDelayMs - Delay between retries in ms (default: 2000)
 * @returns {Promise<{inspectionId: string, status: string, recordingS3Key: string, message: string}>}
 */
export const finalizeInspection = async (
  inspectionId,
  chunkFilenames = null,
  deviceInfo = null,
  maxRetries = 30,
  retryDelayMs = 2000
) => {
  const payload = {};
  if (chunkFilenames && chunkFilenames.length > 0) {
    payload.chunkFilenames = chunkFilenames;
  }
  if (deviceInfo) {
    payload.deviceInfo = deviceInfo;
  }

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await apiClient.post(`/inspections/${inspectionId}/finalize`, payload);

      // Check for 202 Accepted (processing in progress)
      if (response.status === 202) {
        console.log(`Finalize attempt ${attempt}: Processing in progress, retrying in ${retryDelayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        continue;
      }

      console.log('Inspection finalized:', response.data);
      return response.data;
    } catch (error) {
      // Axios treats 202 as success, but check for processing status in response
      if (error.response?.status === 202 || error.response?.data?.status === 'PROCESSING') {
        console.log(`Finalize attempt ${attempt}: Processing in progress, retrying in ${retryDelayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        continue;
      }

      console.error('Error finalizing inspection:', error);
      throw error.response?.data?.detail
        ? new Error(error.response.data.detail)
        : error;
    }
  }

  throw new Error(`Finalize timed out after ${maxRetries} attempts. Audio processing may still be in progress.`);
};

/**
 * Upload file to S3 using presigned URL
 *
 * Note: Uses XMLHttpRequest for progress tracking. This goes directly to S3,
 * not through our API, so it doesn't use apiClient.
 *
 * @param {string} presignedUrl - Presigned S3 URL
 * @param {Blob} blob - Audio blob to upload
 * @param {function} onProgress - Progress callback (optional)
 * @returns {Promise<void>}
 */
export const uploadToS3 = async (presignedUrl, blob, onProgress) => {
  try {
    // Create XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      // Configure and send request
      xhr.open('PUT', presignedUrl);
      // DON'T set Content-Type header - it's already in the presigned URL signature
      // Setting it here triggers CORS preflight which S3 presigned URLs don't handle well
      xhr.send(blob);
    });
  } catch (error) {
    console.error('Error uploading to S3:', error);
    throw error;
  }
};

/**
 * Complete upload process with retry logic
 *
 * New flow:
 * 1. Create inspection (gets backend-generated inspectionId)
 * 2. Get presigned URL
 * 3. Upload to S3
 * 4. Finalize inspection (starts transcription)
 *
 * @param {Object} recordingData - Recording data with blob, vehicleId
 * @param {function} onProgress - Progress callback
 * @param {number} maxRetries - Maximum number of retries (default: 3)
 * @returns {Promise<{success: boolean, inspectionId: string, key: string, error?: string}>}
 */
export const uploadRecording = async (recordingData, onProgress, maxRetries = 3) => {
  const { blob, vehicleId, originalBlob, inspectionId: existingInspectionId, uploadAsRaw } = recordingData;

  // Use vehicleId from recording data, or generate a placeholder if not provided
  const effectiveVehicleId = vehicleId || `VEHICLE-${Date.now()}`;
  const timestamp = generateTimestamp();
  // Use _raw suffix if uploadAsRaw is true - backend will process with FFmpeg
  const filename = uploadAsRaw ? `${timestamp}_raw` : `${timestamp}_recording`;

  // Use existing inspectionId if provided (created earlier in Review.js)
  let inspectionId = existingInspectionId || null;

  // Capture device info once (no input device for file uploads)
  const deviceInfo = captureDeviceInfo();

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Upload attempt ${attempt} of ${maxRetries}`);

      // Step 1: Create inspection (only if not already created)
      if (!inspectionId) {
        if (onProgress) onProgress({ stage: 'creating-inspection', progress: 0 });
        const inspection = await createInspection(effectiveVehicleId, deviceInfo);
        inspectionId = inspection.inspectionId;
        console.log(`Created inspection: ${inspectionId}`);
      } else {
        console.log(`Using existing inspection: ${inspectionId}`);
      }

      // Step 1.5: Upload raw chunk if setting enabled and originalBlob exists
      if (settings.uploadRawChunks.isEnabled() && originalBlob) {
        try {
          const rawFilename = `${timestamp}_raw`;
          const { uploadUrl: rawUploadUrl } = await getPresignedUrl(
            inspectionId,
            rawFilename,
            originalBlob.type || 'audio/webm'
          );
          await uploadToS3(rawUploadUrl, originalBlob);
          console.log(`Raw chunk uploaded: ${rawFilename}`);
        } catch (rawError) {
          // Don't fail the main upload if raw upload fails
          console.warn('Raw chunk upload failed (continuing with processed):', rawError);
        }
      }

      // Step 2: Get presigned URL
      if (onProgress) onProgress({ stage: 'getting-url', progress: 0 });
      const { uploadUrl, key, bucket } = await getPresignedUrl(
        inspectionId,
        filename,
        blob.type || 'audio/webm'
      );

      // Step 3: Upload to S3
      if (onProgress) onProgress({ stage: 'uploading', progress: 0 });

      await uploadToS3(uploadUrl, blob, (progress) => {
        if (onProgress) {
          onProgress({ stage: 'uploading', progress });
        }
      });

      console.log(`Upload successful: ${key}`);

      // Step 4: Finalize inspection (triggers transcription)
      if (onProgress) onProgress({ stage: 'finalizing', progress: 100 });
      await finalizeInspection(inspectionId, null, deviceInfo);

      // Success
      return {
        success: true,
        inspectionId,
        key,
        bucket,
      };

    } catch (error) {
      console.error(`Upload attempt ${attempt} failed:`, error);

      // If this was the last attempt, return error
      if (attempt === maxRetries) {
        return {
          success: false,
          inspectionId, // Return inspectionId even on failure for cleanup
          error: error.message || 'Upload failed',
        };
      }

      // Wait before retrying (exponential backoff)
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      console.log(`Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  return {
    success: false,
    inspectionId,
    error: 'Upload failed after maximum retries',
  };
};

/**
 * Check if upload is possible (network connectivity check)
 *
 * @returns {boolean}
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Queue recording for offline upload
 *
 * @param {Object} recordingData - Recording data to queue
 * @returns {Promise<number>} ID of queued recording
 */
export const queueOfflineUpload = async (recordingData) => {
  try {
    // Import dynamically to avoid circular dependencies
    const { storeRecordingOffline } = await import('./offlineStorage');

    const id = await storeRecordingOffline(recordingData);
    console.log('Recording queued for offline upload with ID:', id);
    return id;
  } catch (error) {
    console.error('Error queuing offline upload:', error);
    throw error;
  }
};

/**
 * Process offline upload queue
 *
 * @param {function} onProgress - Progress callback
 * @returns {Promise<{processed: number, failed: number}>} Upload results
 */
export const processOfflineQueue = async (onProgress) => {
  try {
    // Import dynamically to avoid circular dependencies
    const { getPendingUploads, getRecordingById, deleteRecording, incrementRetryCount } = await import('./offlineStorage');

    const pendingUploads = await getPendingUploads();

    if (pendingUploads.length === 0) {
      console.log('No pending uploads to process');
      return { processed: 0, failed: 0 };
    }

    console.log(`Processing ${pendingUploads.length} pending uploads`);

    let processed = 0;
    let failed = 0;

    for (const record of pendingUploads) {
      try {
        if (onProgress) {
          onProgress({
            stage: 'processing-queue',
            current: processed + failed + 1,
            total: pendingUploads.length,
          });
        }

        // Get the full recording data including blob
        const recordingData = await getRecordingById(record.id);

        // Attempt to upload with 1 retry (already tried at least once)
        const result = await uploadRecording(recordingData, (progress) => {
          if (onProgress) {
            onProgress({
              stage: 'uploading-queued',
              current: processed + failed + 1,
              total: pendingUploads.length,
              progress: progress.progress,
            });
          }
        }, 1);

        if (result.success) {
          // Delete from offline storage after successful upload
          await deleteRecording(record.id);
          processed++;
          console.log(`Successfully uploaded queued recording: ${record.vehicleId}`);
        } else {
          // Increment retry count but keep in queue
          await incrementRetryCount(record.id);
          failed++;
          console.error(`Failed to upload queued recording: ${record.vehicleId}`, result.error);
        }
      } catch (error) {
        console.error('Error processing queued upload:', error);
        failed++;
      }
    }

    console.log(`Offline queue processed: ${processed} successful, ${failed} failed`);
    return { processed, failed };
  } catch (error) {
    console.error('Error processing offline queue:', error);
    return { processed: 0, failed: 0 };
  }
};
